import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/indentCreationAPI';

// api/*.js re-throws err.response.data on HTTP failures, which is the backend's
// ResultEntity shape ({ Success, Message, ErrorDetails: { ErrorMessage } }) — not a
// plain Error, so `.message` is always undefined there. Read the real field instead.
const getErrorMessage = (err, fallback) =>
    err?.ErrorDetails?.ErrorMessage || err?.Message || err?.message || fallback;

// ── Shared mapper ─────────────────────────────────────────────────────────────

const mapLineItem = (item, meta = {}) => ({
    tempId: item.IndentListId || (Date.now() + Math.random()),
    IndentListId: item.IndentListId || null,
    ItemCode: (item.ItemCode || meta.ItemCode || '').trim(),
    ItemName: item.ItemName || meta.ItemName || '',
    Specification: item.Specification || '',
    DcaCode: item.DcaCode || item.Dca || '',
    SubDcaCode: item.SubDcaCode || item.SubDca || '',
    CategoryCode: item.CategoryCode || meta.CategoryCode || '',
    CategoryName: item.CategoryName || meta.CategoryName || '',
    CapitalMaterialType: item.CapitalMasterType || meta.CapitalMaterialType || '',
    CCCode: item.Costcenter || meta.Costcenter || '',
    BasicPrice: parseFloat(item.BasicPrice || 0),
    Units: item.Units || '',
    Qty: parseFloat(item.Quantity || item.Qty || 0),
    Amount: parseFloat(item.Amount || 0),
});

// ── Thunks ────────────────────────────────────────────────────────────────────

export const fetchCCType = createAsyncThunk(
    'indentCreation/fetchCCType',
    async (CCType, { rejectWithValue }) => {
        try {
            const res = await api.getCostCenterType(CCType);
            const data = res?.Data;
            return Array.isArray(data) ? data[0] : data;
        } catch (err) { return rejectWithValue(getErrorMessage(err, 'Failed to fetch CC type')); }
    }
);

// Gates the page: 'NPCC' roles use a structurally different Central-Store-Keeper
// consolidation flow that this page does not implement.
export const fetchIndentType = createAsyncThunk(
    'indentCreation/fetchIndentType',
    async (roleId, { rejectWithValue }) => {
        try {
            const res = await api.getIndentFirstLevelDetails(roleId);
            const data = res?.Data;
            const record = Array.isArray(data) ? data[0] : data;
            return record?.IndentType || record?.indentType || '';
        } catch (err) { return rejectWithValue(getErrorMessage(err, 'Failed to determine indent workflow')); }
    }
);

export const fetchMaterialItemCodes = createAsyncThunk(
    'indentCreation/fetchMaterialItemCodes',
    async (materialType, { rejectWithValue }) => {
        try {
            const res = await api.getMaterialItemCodes(materialType);
            return res?.Data || [];
        } catch (err) { return rejectWithValue(getErrorMessage(err, 'Failed to fetch master item codes')); }
    }
);

export const fetchCategoriesCapital = createAsyncThunk(
    'indentCreation/fetchCategoriesCapital',
    async ({ cType, materialType }, { rejectWithValue }) => {
        try {
            const res = await api.getCategoriesCapital(cType, materialType);
            return res?.Data || [];
        } catch (err) { return rejectWithValue(getErrorMessage(err, 'Failed to fetch categories')); }
    }
);

export const fetchCapitalMasterTypes = createAsyncThunk(
    'indentCreation/fetchCapitalMasterTypes',
    async (Type, { rejectWithValue }) => {
        try {
            const res = await api.getCapitalMasterTypes(Type);
            return res?.Data || [];
        } catch (err) { return rejectWithValue(getErrorMessage(err, 'Failed to fetch capital types')); }
    }
);

export const fetchStagedItems = createAsyncThunk(
    'indentCreation/fetchStagedItems',
    async ({ CCode, CapitalItemcode, Materialtype }, { rejectWithValue }) => {
        try {
            const res = await api.getItemCodesByCC(CCode, CapitalItemcode, Materialtype);
            return res?.Data || [];
        } catch (err) { return rejectWithValue(getErrorMessage(err, 'Failed to load staged items')); }
    }
);

export const fetchIndentCategories = createAsyncThunk(
    'indentCreation/fetchCategories',
    async (CType, { rejectWithValue }) => {
        try {
            const res = await api.getIndentCategories(CType);
            return res?.Data || [];
        } catch (err) { return rejectWithValue(getErrorMessage(err, 'Failed to fetch categories')); }
    }
);

export const searchItems = createAsyncThunk(
    'indentCreation/searchItems',
    async ({ Pfx, Typ, Cat }, { rejectWithValue }) => {
        try {
            const res = await api.getAutoComplete(Pfx, Typ, Cat);
            return res?.Data || [];
        } catch (err) { return rejectWithValue(getErrorMessage(err, 'Failed to search items')); }
    }
);

// payload: { ItemCode, Costcenter, CapitalMaterialType, CapitalMaterials, ItemName, CategoryCode, CategoryName }
export const addIndentItem = createAsyncThunk(
    'indentCreation/addItem',
    async (payload, { rejectWithValue }) => {
        let raw;
        try {
            const res = await api.saveIndentItems({
                ItemCode: payload.ItemCode,
                Costcenter: payload.Costcenter,
                // spInsertIndentItems branches on IS NULL — an empty string is NOT
                // NULL in SQL, so these must be real null when the flow doesn't apply
                // (never '') or the insert silently lands in the wrong branch.
                CapitalMaterialType: payload.CapitalMaterialType || null,
                CapitalMaterials: payload.CapitalMaterials || null,
            });
            raw = res?.Data ?? res;
        } catch (err) {
            return rejectWithValue(getErrorMessage(err, 'Failed to add item'));
        }

        const rawStr = typeof raw === 'string' ? raw : '';
        if (rawStr.split(',')[0]?.trim().toLowerCase() !== 'submited') {
            return rejectWithValue(rawStr || 'Failed to add item');
        }

        // Insert succeeded server-side. Refreshing the authoritative list is a
        // separate, best-effort step — if *this* fails, the add itself must not be
        // reported as failed (the item is already saved; only the on-screen list
        // is stale), otherwise a transient read error masks a successful write.
        try {
            const listRes = await api.getItemCodesByCC(
                payload.Costcenter,
                payload.CapitalMaterials || null,
                payload.CapitalMaterialType || null,
            );
            const stagedList = listRes?.Data || null;
            return {
                raw,
                itemMeta: payload,
                stagedList,
                inserted: true,
                // Empty-but-not-thrown is just as much "the list didn't reflect the
                // add" as a hard failure — flag it the same way so the page retries.
                refreshFailed: !Array.isArray(stagedList) || stagedList.length === 0,
            };
        } catch (err) {
            return { raw, itemMeta: payload, stagedList: null, inserted: true, refreshFailed: true };
        }
    }
);

export const removeIndentItem = createAsyncThunk(
    'indentCreation/removeItem',
    async ({ IndentListId, tempId }, { rejectWithValue }) => {
        try {
            if (IndentListId) {
                await api.deleteIndentItem({ IndentListId });
            }
            return tempId;
        } catch (err) { return rejectWithValue(getErrorMessage(err, 'Failed to delete item')); }
    }
);

export const submitIndent = createAsyncThunk(
    'indentCreation/submit',
    async (payload, { rejectWithValue }) => {
        try {
            const res = await api.saveIndent(payload);
            return res?.Data ?? res;
        } catch (err) { return rejectWithValue(getErrorMessage(err, 'Failed to submit indent')); }
    }
);

// ── Initial State ─────────────────────────────────────────────────────────────

const initialState = {
    indentType: null,
    ccType: null,
    capitalMasterTypes: [],
    materialItemCodes: [],
    categories: [],
    searchResults: [],
    stagedItems: [],
    // Set when an item was saved server-side but the follow-up list refresh failed —
    // the page watches this to re-issue fetchStagedItems automatically.
    addItemNeedsRefresh: false,
    // Set to the item code every time an insert succeeds, regardless of whether the
    // list refresh worked — lets the page confirm "saved" independent of "displayed".
    lastAddedItemCode: null,
    submitResult: null,
    submitIndentNo: null,
    loading: {
        indentType: false,
        ccType: false,
        capitalTypes: false,
        materialItemCodes: false,
        categories: false,
        search: false,
        stagedItems: false,
        addItem: false,
        removeItem: false,
        submit: false,
    },
    errors: {
        indentType: null,
        ccType: null,
        capitalTypes: null,
        materialItemCodes: null,
        categories: null,
        search: null,
        stagedItems: null,
        addItem: null,
        removeItem: null,
        submit: null,
    },
};

// ── Slice ─────────────────────────────────────────────────────────────────────

const indentCreationSlice = createSlice({
    name: 'indentCreation',
    initialState,
    reducers: {
        updateItemQty: (state, action) => {
            const { tempId, qty } = action.payload;
            const item = state.stagedItems.find(i => i.tempId === tempId);
            if (item) {
                item.Qty = parseFloat(qty) || 0;
                if (item.BasicPrice > 0) {
                    item.Amount = parseFloat((item.BasicPrice * item.Qty).toFixed(2));
                }
            }
        },
        updateItemAmount: (state, action) => {
            const { tempId, amount } = action.payload;
            const item = state.stagedItems.find(i => i.tempId === tempId);
            if (item) item.Amount = parseFloat(amount) || 0;
        },
        clearSearchResults: (state) => {
            state.searchResults = [];
        },
        // Used when the Cost Center changes — clears everything scoped to the previous
        // CC selection but keeps `indentType`, which only depends on the user's role
        // and shouldn't be re-fetched (and briefly re-gate the page) on every CC change.
        clearCCScopedState: (state) => {
            state.ccType = null;
            state.capitalMasterTypes = [];
            state.materialItemCodes = [];
            state.categories = [];
            state.searchResults = [];
            state.stagedItems = [];
            state.addItemNeedsRefresh = false;
            state.errors.ccType = null;
            state.errors.capitalTypes = null;
            state.errors.materialItemCodes = null;
            state.errors.categories = null;
        },
        clearAddItemError: (state) => {
            state.errors.addItem = null;
        },
        clearAddItemNeedsRefresh: (state) => {
            state.addItemNeedsRefresh = false;
        },
        clearLastAddedItemCode: (state) => {
            state.lastAddedItemCode = null;
        },
        clearSubmitResult: (state) => {
            state.submitResult = null;
            state.submitIndentNo = null;
            state.errors.submit = null;
        },
        resetIndentCreation: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            // fetchIndentType
            .addCase(fetchIndentType.pending, (s) => { s.loading.indentType = true; s.errors.indentType = null; })
            .addCase(fetchIndentType.fulfilled, (s, a) => { s.loading.indentType = false; s.indentType = a.payload; })
            .addCase(fetchIndentType.rejected, (s, a) => { s.loading.indentType = false; s.errors.indentType = a.payload; })

            // fetchMaterialItemCodes
            .addCase(fetchMaterialItemCodes.pending, (s) => { s.loading.materialItemCodes = true; s.errors.materialItemCodes = null; s.materialItemCodes = []; })
            .addCase(fetchMaterialItemCodes.fulfilled, (s, a) => { s.loading.materialItemCodes = false; s.materialItemCodes = a.payload; })
            .addCase(fetchMaterialItemCodes.rejected, (s, a) => { s.loading.materialItemCodes = false; s.errors.materialItemCodes = a.payload; })

            // fetchCategoriesCapital (shares the `categories` slot with fetchIndentCategories)
            .addCase(fetchCategoriesCapital.pending, (s) => { s.loading.categories = true; s.errors.categories = null; s.categories = []; })
            .addCase(fetchCategoriesCapital.fulfilled, (s, a) => { s.loading.categories = false; s.categories = a.payload; })
            .addCase(fetchCategoriesCapital.rejected, (s, a) => { s.loading.categories = false; s.errors.categories = a.payload; })

            // fetchCCType
            .addCase(fetchCCType.pending, (s) => {
                s.loading.ccType = true; s.errors.ccType = null;
                s.ccType = null; s.capitalMasterTypes = []; s.categories = [];
            })
            .addCase(fetchCCType.fulfilled, (s, a) => { s.loading.ccType = false; s.ccType = a.payload; })
            .addCase(fetchCCType.rejected, (s, a) => { s.loading.ccType = false; s.errors.ccType = a.payload; })

            // fetchCapitalMasterTypes
            .addCase(fetchCapitalMasterTypes.pending, (s) => {
                s.loading.capitalTypes = true; s.errors.capitalTypes = null; s.capitalMasterTypes = []; s.categories = [];
            })
            .addCase(fetchCapitalMasterTypes.fulfilled, (s, a) => { s.loading.capitalTypes = false; s.capitalMasterTypes = a.payload; })
            .addCase(fetchCapitalMasterTypes.rejected, (s, a) => { s.loading.capitalTypes = false; s.errors.capitalTypes = a.payload; })

            // fetchIndentCategories
            .addCase(fetchIndentCategories.pending, (s) => {
                s.loading.categories = true; s.errors.categories = null; s.categories = [];
            })
            .addCase(fetchIndentCategories.fulfilled, (s, a) => { s.loading.categories = false; s.categories = a.payload; })
            .addCase(fetchIndentCategories.rejected, (s, a) => { s.loading.categories = false; s.errors.categories = a.payload; })

            // searchItems
            .addCase(searchItems.pending, (s) => { s.loading.search = true; s.errors.search = null; s.searchResults = []; })
            .addCase(searchItems.fulfilled, (s, a) => { s.loading.search = false; s.searchResults = a.payload; })
            .addCase(searchItems.rejected, (s, a) => { s.loading.search = false; s.errors.search = a.payload; })

            // fetchStagedItems
            .addCase(fetchStagedItems.pending, (s) => { s.loading.stagedItems = true; s.errors.stagedItems = null; })
            .addCase(fetchStagedItems.fulfilled, (s, a) => {
                s.loading.stagedItems = false;
                s.stagedItems = a.payload.map(item => mapLineItem(item));
            })
            .addCase(fetchStagedItems.rejected, (s, a) => { s.loading.stagedItems = false; s.errors.stagedItems = a.payload; })

            // addIndentItem
            .addCase(addIndentItem.pending, (s) => { s.loading.addItem = true; s.errors.addItem = null; })
            .addCase(addIndentItem.fulfilled, (s, a) => {
                s.loading.addItem = false;
                const { itemMeta, stagedList, refreshFailed } = a.payload;
                // Fires a toast on the page independent of whether the list refresh
                // below actually worked — so "saved" and "displayed" are never conflated.
                s.lastAddedItemCode = itemMeta.ItemCode;

                if (Array.isArray(stagedList) && stagedList.length > 0) {
                    // Authoritative list from GetItemCodesbyCC — preserves user's existing Qty/Amount
                    const existing = new Map(s.stagedItems.map(i => [i.IndentListId, i]));
                    s.stagedItems = stagedList.map(item => {
                        const mapped = mapLineItem(item, itemMeta);
                        const prev = existing.get(mapped.IndentListId);
                        return prev
                            ? { ...mapped, Qty: prev.Qty, Amount: prev.Amount }
                            : mapped;
                    });
                } else if (refreshFailed) {
                    // The item IS saved server-side — only the on-screen list is stale
                    // (the refresh either errored, or came back with zero rows). The
                    // page watches this flag and re-issues fetchStagedItems itself.
                    s.addItemNeedsRefresh = true;
                }
            })
            .addCase(addIndentItem.rejected, (s, a) => { s.loading.addItem = false; s.errors.addItem = a.payload; })

            // removeIndentItem
            .addCase(removeIndentItem.pending, (s) => { s.loading.removeItem = true; s.errors.removeItem = null; })
            .addCase(removeIndentItem.fulfilled, (s, a) => {
                s.loading.removeItem = false;
                s.stagedItems = s.stagedItems.filter(i => i.tempId !== a.payload);
            })
            .addCase(removeIndentItem.rejected, (s, a) => { s.loading.removeItem = false; s.errors.removeItem = a.payload; })

            // submitIndent
            .addCase(submitIndent.pending, (s) => {
                s.loading.submit = true; s.errors.submit = null;
                s.submitResult = null; s.submitIndentNo = null;
            })
            .addCase(submitIndent.fulfilled, (s, a) => {
                s.loading.submit = false;
                const raw = typeof a.payload === 'string' ? a.payload : '';
                const parts = raw.split(',');
                if (parts[0]?.trim().toLowerCase() === 'submited') {
                    s.submitResult = 'Submited';
                    s.submitIndentNo = parts[1]?.trim() || null;
                    s.stagedItems = [];
                } else {
                    s.errors.submit = raw || 'Submission failed';
                }
            })
            .addCase(submitIndent.rejected, (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    updateItemQty,
    updateItemAmount,
    clearSearchResults,
    clearCCScopedState,
    clearAddItemError,
    clearAddItemNeedsRefresh,
    clearLastAddedItemCode,
    clearSubmitResult,
    resetIndentCreation,
} = indentCreationSlice.actions;


// ── Selectors ─────────────────────────────────────────────────────────────────

export const selectIndentType            = (s) => s.indentCreation.indentType;
export const selectCCType                = (s) => s.indentCreation.ccType;
export const selectCapitalMasterTypes    = (s) => s.indentCreation.capitalMasterTypes;
export const selectMaterialItemCodes     = (s) => s.indentCreation.materialItemCodes;
export const selectIndentCategories      = (s) => s.indentCreation.categories;
export const selectSearchResults         = (s) => s.indentCreation.searchResults;
export const selectStagedItems           = (s) => s.indentCreation.stagedItems;
export const selectAddItemNeedsRefresh   = (s) => s.indentCreation.addItemNeedsRefresh;
export const selectLastAddedItemCode     = (s) => s.indentCreation.lastAddedItemCode;
export const selectSubmitResult          = (s) => s.indentCreation.submitResult;
export const selectSubmitIndentNo        = (s) => s.indentCreation.submitIndentNo;
export const selectIndentCreationLoading = (s) => s.indentCreation.loading;
export const selectIndentCreationErrors  = (s) => s.indentCreation.errors;

export default indentCreationSlice.reducer;
